import {
  AccountInfo,
  ActivateStatus,
  getTransaction,
  GetTransactionData,
  TransactionId,
} from 'api/v1/account'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'store'
import { TokenSymbol } from 'types'
import { sleep } from 'utils/sleep'
import { AccountTickets, CurrencyBalance } from './types'

export function useZkexConnecting() {
  return useSelector<RootState, boolean>((state) => state.account.connecting)
}
export function useAccountInfo() {
  const accountInfo = useSelector<RootState, AccountInfo | null>(
    (state) => state.account.accountInfo,
  )
  return useMemo(() => {
    return accountInfo ?? ({} as Partial<AccountInfo>)
  }, [accountInfo])
}
export function useUserId() {
  const { userId = 0 } = useAccountInfo()
  return userId
}
export function useAccountId() {
  const { accountId } = useAccountInfo()
  return accountId
}
export function useL2AccountId() {
  const { layer2Id = 0 } = useAccountInfo()
  return layer2Id
}
export function useActivateStatus() {
  const { status = 0 } = useAccountInfo()
  return status
}
export function useIsActivated() {
  const { status = 0 } = useAccountInfo()
  return status === ActivateStatus.ACTIVATION_SUCCESS
}
export function useActivating() {
  const { status = 0 } = useAccountInfo()
  const activating = useSelector<RootState, boolean>(
    (state) => state.account.activating,
  )
  return activating || status === ActivateStatus.ACTIVATING
}
export function useAccountBalances() {
  return useSelector<RootState, CurrencyBalance[]>(
    (state) => state.account.balances,
  )
}
export function useCurrencyBalance(symbol: TokenSymbol) {
  const balances = useAccountBalances()
  return balances.find((v) => v.currency === symbol)
}
export function useAccountTickets() {
  return useSelector<RootState, AccountTickets | undefined>(
    (state) => state.account.tickets,
  )
}
export function useIsLogin() {
  const { userId = 0 } = useAccountInfo()
  return !!userId
}
export function useUserAuthConnected() {
  const isLogin = useIsLogin()
  const tickets = useAccountTickets()
  return isLogin || !!tickets
}
export function useUserToken() {
  return useSelector<RootState, string>((state) => state.account.token)
}

export async function waitTransaction(txId: TransactionId) {
  let data: GetTransactionData
  do {
    data = await getTransaction(txId)
    if (data?.txStatus !== 'SUCCESS' && data?.txStatus !== 'FAILED') {
      await sleep(2000)
    }
  } while (data?.txStatus !== 'SUCCESS' && data?.txStatus !== 'FAILED')

  return data
}
